import { EntityDetailValueDto, PedigreeNodeDto } from '@/apiClient';
import { ENTITY_DETAIL_NAME } from '@/model/form-builder';
import { formatDateISOString } from '@/lib/dateTime';
import { parseJson } from '@/lib/common';
import { MAX_PEDIGREE_DEPTH } from './constants';
import type { PedigreeEntry } from './types';

/** Text the registry uses for an unknown sire/dam slot. */
const UNKNOWN_ANIMAL_NAME = 'Unknown';

/** Picks the fields we need out of a registry node's generic `tags` array. */
function parseEntityDetails(tags: EntityDetailValueDto[]) {
  let breederId, dob, entityId, ident, sex, status, tattoo;

  tags.forEach(
    ({ additionalInfo, entityDetailName, entityId: tagEntityId, value }) => {
      switch (entityDetailName) {
        case ENTITY_DETAIL_NAME.ANIMAL_IDENT:
          ident = value || '';
          break;
        case ENTITY_DETAIL_NAME.ANIMAL_STATUS:
          status = value || '';
          break;
        case ENTITY_DETAIL_NAME.BREEDER:
          breederId = additionalInfo?.id;
          break;
        case ENTITY_DETAIL_NAME.DATE_OF_BIRTH:
          dob = value ? formatDateISOString(value) : '';
          break;
        case ENTITY_DETAIL_NAME.SEX_CODE:
          sex = parseJson(value, {}).label || '';
          break;
        case ENTITY_DETAIL_NAME.TATTOO:
          tattoo = value || '';
          break;
      }

      if (tagEntityId != null) entityId = tagEntityId;
    },
  );

  return { breederId, dob, entityId, ident, sex, status, tattoo };
}

/**
 * Returns `undefined` when the node has no usable identity — the chart
 * renders an "Unknown" placeholder in that slot.
 */
export function adaptPedigreeNode(
  node: PedigreeNodeDto,
  depth: number = 0,
  maxDepth: number = MAX_PEDIGREE_DEPTH,
): PedigreeEntry | undefined {
  const parsed = parseEntityDetails(node.tags ?? []);
  const name = node.animalName ?? '';
  const ident = parsed.ident ?? null;
  if ((!name || name === UNKNOWN_ANIMAL_NAME) && !ident) return undefined;
  if (parsed.entityId == null) return undefined;
  const recurse = depth < maxDepth - 1;
  return {
    id: parsed.entityId,
    name,
    ident,
    sex: parsed.sex ?? null,
    dob: parsed.dob ?? null,
    status: parsed.status ?? null,
    sire:
      recurse && node.sire
        ? adaptPedigreeNode(node.sire, depth + 1, maxDepth)
        : undefined,
    dam:
      recurse && node.dam
        ? adaptPedigreeNode(node.dam, depth + 1, maxDepth)
        : undefined,
  };
}
